import { createSelector, createSlice } from '@reduxjs/toolkit'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import mapApi from '~map-preview/services/map'
import { Map } from '~map-preview/types/__generated/gql/graphql'
import { RootState } from '../store'
import { isMapstackProperty } from 'utils'

type State = {
  mapData?: Map
  datasets: string[]
  jsonFeatures: any[]
  currentFeature: any
  isMapSaved: boolean
  isMapDeleting: boolean
  bounds?: [number, number, number, number]
  categories?: Record<string, string[]>
}

export const initialMapState: State = {
  mapData: undefined,
  datasets: [],
  jsonFeatures: [],
  currentFeature: {},
  isMapSaved: false,
  bounds: undefined,
  categories: undefined,
  isMapDeleting: false
}

export const mapSlice = createSlice({
  name: 'map',
  initialState: initialMapState,
  reducers: {
    setIsMapSaved: (state, action) => {
      state.isMapSaved = action.payload
    },

    setMapData: (state, action) => {
      state.mapData = action.payload
    },

    setJsonFeatures: (state, action) => {
      state.jsonFeatures = action.payload
    },

    addDataset: (state, action) => {
      state.datasets.push(action.payload)
    },

    setCurrentFeature: (state, action) => {
      state.currentFeature = action.payload
    },

    setLiked: (state, action) => {
      if (state.mapData) state.mapData.liked = action.payload
    },

    setReadme: (state, action) => {
      if (state.mapData?.readme?.nodes)
        state.mapData.readme.nodes = action.payload
    },

    setBounds: (state, action) => {
      const bounds = action.payload

      if (!bounds) return

      if (
        Object.values(bounds).some(
          (value) => value === Infinity || value === -Infinity
        )
      )
        return

      state.bounds = bounds
    },

    increaseLikes: (state) => {
      if (state.mapData) state.mapData.likes++
    },

    decreaseLikes: (state) => {
      if (state.mapData) state.mapData.likes--
    },

    setCategories: (state, action) => {
      state.categories = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(mapApi.endpoints.deleteMapById.matchPending, (state) => {
        state.isMapDeleting = true
      })
      .addMatcher(mapApi.endpoints.deleteMapById.matchFulfilled, (state) => {
        state.isMapDeleting = false
      })
      .addMatcher(mapApi.endpoints.deleteMapById.matchRejected, (state) => {
        state.isMapDeleting = false
      })
  }
})

export const {
  setIsMapSaved,
  setMapData,
  addDataset,
  setJsonFeatures,
  setCurrentFeature,
  setLiked,
  setReadme,
  setBounds,
  increaseLikes,
  decreaseLikes,
  setCategories
} = mapSlice.actions

const selectCurrentFeature = (state) => state.map.currentFeature

const selectDataSets = (state) => state.map.datasets

const selectIsMapDeleting = (state) => state.map.isMapDeleting

const selectIsMapSaved = (state) => state.map.isMapSaved

const selectJsonFeatures = (state) => state.map.jsonFeatures

const selectMapData = createSelector(
  (state: RootState) => state.map.mapData,
  (mapData) => {
    return mapData
  }
)

const selectMapId = createSelector(selectMapData, (mapData) => {
  return mapData?.id as string
})

const selectNumericProperties = createSelector(selectMapData, (mapData) => {
  if (!mapData?.properties) return []
  const numericTypes = ['integer', 'float']
  return _.filter(
    mapData?.properties,
    (item) =>
      // INFO: excludes mapstack properties from numeric properties
      _.includes(numericTypes, item.type) && !isMapstackProperty(item.name)
  )
})

const selectProperties = createSelector(
  (state: RootState) => state.map.mapData?.properties,
  (properties) => {
    if (!properties) return []
    return properties.filter((property) => !isMapstackProperty(property.name))
  }
)

const selectReadmeNodes = createSelector(selectMapData, (mapData) => {
  return mapData?.readme?.nodes
})

const selectTitleProperty = createSelector(selectProperties, (properties) => {
  return properties.find((property) => property.isTitle)
})

const selectVisualisations = createSelector(selectProperties, (properties) => {
  return properties.filter((property) => property.visualisation !== 'none')
})

const selectBounds = (state) => state.map.bounds

export const selectCategories = (state) => state.map.categories

const selectCategoryOptions = createSelector(selectCategories, (categories) => {
  return (
    Object.keys(categories ?? {}).map((key) => ({
      label: key,
      value: key
    })) || []
  )
})

export const selectCategoryCounts = createSelector(
  selectCategories,
  (categories) => {
    return Object.keys(categories ?? {}).length
  }
)

export const useCurrentFeature = () => useSelector(selectCurrentFeature)
export const useDataSets = () => useSelector(selectDataSets)
export const useIsMapSaved = () => useSelector(selectIsMapSaved)
export const useJsonFeatures = () => useSelector(selectJsonFeatures)
export const useMap = () => useSelector(selectMapData) as Map
export const useMapId = () => useSelector(selectMapId)
export const useNumericProperties = () => useSelector(selectNumericProperties)
export const useProperties = () => useSelector(selectProperties)
export const useReadmeNodes = () => useSelector(selectReadmeNodes)
export const useTitleProperty = () => useSelector(selectTitleProperty)
export const useVisualisations = () => useSelector(selectVisualisations)
export const useBounds = () => useSelector(selectBounds)
export const useCategoryOptions = () => useSelector(selectCategoryOptions)
export const useIsMapDeleting = () => useSelector(selectIsMapDeleting)

export default mapSlice.reducer
